import { useState } from "react";
import { motion } from "framer-motion";
import { Share2, MessageCircle, Linkedin, Mail, Copy, Check, Gift } from "lucide-react";

/**
 * Referral / share-with-procurement widget.
 *
 * Purpose: nudge an existing lead to share Ezeefit with a colleague (HR,
 * facility manager, architect partner, or another procurement contact).
 * Every share is a free brand-impression and a potential branded-search
 * — both of which compound on-page SEO.
 *
 * Mechanics: client-side share — opens the platform's native composer
 * with a pre-filled message + a `?ref=share` URL.  No backend storage,
 * no email collection — friction-free.
 *
 * The 5% discount is an honest promise to the *referrer* on their next
 * order (verified manually by the sales team during quote).
 */

const SHARE_URL = "https://ezeefitofficesystems.in/?ref=share";
const SHARE_MESSAGE =
  "I just looked at Ezeefit Office Systems — a modular office furniture manufacturer in Mumbai (factory in Bhiwandi, ISO 9001:2015 + IGBC certified). Worth a look if your team is planning an office fit-out. ";

export default function ReferralWidget() {
  const [copied, setCopied] = useState(false);

  const handleWhatsApp = () => {
    const text = encodeURIComponent(`${SHARE_MESSAGE}${SHARE_URL}`);
    window.open(`https://wa.me/?text=${text}`, "_blank", "noopener,noreferrer");
  };

  const handleLinkedIn = () => {
    const url = encodeURIComponent(SHARE_URL);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, "_blank", "noopener,noreferrer");
  };

  const handleEmail = () => {
    const subject = encodeURIComponent("Worth a look — Ezeefit Office Systems");
    const body = encodeURIComponent(`${SHARE_MESSAGE}\n\n${SHARE_URL}\n\n— Sent via Ezeefit's procurement-share programme`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${SHARE_MESSAGE}${SHARE_URL}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for older browsers / non-HTTPS environments
      const ta = document.createElement("textarea");
      ta.value = `${SHARE_MESSAGE}${SHARE_URL}`;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.2 }}
      className="mt-8 bg-white border-2 border-[#F97316] p-6 md:p-8 relative overflow-hidden"
      data-testid="referral-widget"
    >
      {/* Decorative corner badge */}
      <div className="absolute -top-1 -right-1 bg-[#F97316] text-white text-[10px] font-mono uppercase tracking-widest px-4 py-1.5">
        Referral Programme
      </div>

      <div className="flex flex-col md:flex-row gap-6 md:gap-8 items-start">
        {/* Icon block */}
        <div className="shrink-0 w-14 h-14 bg-[#FFF7ED] border border-[#F97316] flex items-center justify-center">
          <Gift className="w-7 h-7 text-[#F97316]" strokeWidth={1.75} />
        </div>

        {/* Content */}
        <div className="flex-1 pt-1">
          <h3 className="text-xl md:text-2xl font-bold text-[#0F172A] tracking-tight mb-2">
            Save 5% on Your First Order
          </h3>
          <p className="text-sm md:text-base text-[#475569] leading-relaxed mb-5">
            Share Ezeefit with another procurement team, facility manager or
            architect partner. When they sign their first project quote, we
            credit <strong className="text-[#0F172A]">5% off your next
            order</strong> — no minimum, no expiry.
          </p>

          {/* Share buttons */}
          <div className="flex flex-wrap gap-2.5">
            <button
              type="button"
              onClick={handleWhatsApp}
              data-testid="referral-share-whatsapp"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#25D366] hover:bg-[#1DA851] text-white text-xs font-semibold uppercase tracking-wider transition-colors"
            >
              <MessageCircle className="w-4 h-4" strokeWidth={2.25} />
              WhatsApp
            </button>
            <button
              type="button"
              onClick={handleLinkedIn}
              data-testid="referral-share-linkedin"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0A66C2] hover:bg-[#084E96] text-white text-xs font-semibold uppercase tracking-wider transition-colors"
            >
              <Linkedin className="w-4 h-4" strokeWidth={2.25} />
              LinkedIn
            </button>
            <button
              type="button"
              onClick={handleEmail}
              data-testid="referral-share-email"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#0F172A] hover:bg-[#1E293B] text-white text-xs font-semibold uppercase tracking-wider transition-colors"
            >
              <Mail className="w-4 h-4" strokeWidth={2.25} />
              Email
            </button>
            <button
              type="button"
              onClick={handleCopy}
              data-testid="referral-share-copy"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-[#F8FAFC] border border-[#0F172A] text-[#0F172A] text-xs font-semibold uppercase tracking-wider transition-colors"
            >
              {copied ? (
                <>
                  <Check className="w-4 h-4 text-[#16A34A]" strokeWidth={2.25} />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" strokeWidth={2.25} />
                  Copy Link
                </>
              )}
            </button>
          </div>

          <p className="mt-4 text-[11px] text-[#94A3B8] leading-relaxed flex items-start gap-1.5">
            <Share2 className="w-3 h-3 mt-0.5 shrink-0" strokeWidth={2} />
            5% credit is applied at the time of your next Ezeefit quote — please mention the referred company name to our sales team.
          </p>
        </div>
      </div>
    </motion.div>
  );
}
